import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "primereact/button";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useAppContext } from "../../providers/ProviderAppContainer";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import FileInput from "../common/FileInput";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import UserSearch from "../common/UserSearch";
import DialogTaskComment from "./DialogTaskComment";
import { TEXT_NORMAL, TEXT_SMALL, TEXT_SUBTITLE } from "../../style";
import { getReadableDate, getWriteableDate, hasRequiredAuthority, toAppDate } from "../../utils";
import { AUTHORITIES, EMPTY_VALUE, TASK_PRIORITIES } from "../../constants";

const EMPTY_USER_TASK_STATUSES = [];
const LIST_SCOPES = ["assigned", "created", "all"];

function listPathForScope(scope) {
    return LIST_SCOPES.includes(scope) ? `/user-tasks/${scope}` : "/user-tasks/assigned";
}

export default function TaskDetail() {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { requestAPI, showToast } = useAppContext();
    const { id: loggedInUserId, authorities = [] } = useSelector((state) => state.stateUser);
    const statuses = useSelector((state) => state.stateTemplateConfig?.global?.user_task_statuses ?? EMPTY_USER_TASK_STATUSES);
    const fromScope = LIST_SCOPES.includes(location.state?.fromScope) ? location.state.fromScope : null;
    const canReadAll = hasRequiredAuthority(authorities, AUTHORITIES.READ_ALL_USER_TASKS);

    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [deleting, setDeleting] = useState();
    const [error, setError] = useState();
    const [task, setTask] = useState();
    const [draft, setDraft] = useState();
    const [comments, setComments] = useState([]);
    const [commentLoading, setCommentLoading] = useState();
    const [assignee, setAssignee] = useState(null);
    const [dialogComment, setDialogComment] = useState({ visible: false });

    const isCreator = useMemo(() => task && Number(task.created_by) === Number(loggedInUserId), [task, loggedInUserId]);
    const isAssignee = useMemo(() => task && Number(task.user_id) === Number(loggedInUserId), [task, loggedInUserId]);
    const canEditStatus = useMemo(() => isCreator || isAssignee, [isAssignee, isCreator]);
    const isReadOnlyViewer = useMemo(() => task && !isCreator && !isAssignee && canReadAll, [canReadAll, isAssignee, isCreator, task]);
    const canComment = useMemo(() => Boolean(task && (isCreator || isAssignee || canReadAll)), [canReadAll, isAssignee, isCreator, task]);
    const isOwnComment = useCallback((comment) => Number(comment?.created_by) === Number(loggedInUserId), [loggedInUserId]);

    const statusOptions = useMemo(() => {
        if (statuses?.length) return statuses;
        if (task?.status_id) {
            return [{ id: task.status_id, title: task.status_title || `Status #${task.status_id}` }];
        }
        return EMPTY_USER_TASK_STATUSES;
    }, [statuses, task]);

    const selectedStatus = useMemo(
        () => statusOptions.find((s) => Number(s.id) === Number(draft?.status_id)) || null,
        [draft?.status_id, statusOptions],
    );

    const selectedPriority = useMemo(
        () => TASK_PRIORITIES.find((p) => Number(p.id) === Number(draft?.priority)) || null,
        [draft?.priority],
    );

    const goToList = useCallback(() => {
        navigate(listPathForScope(fromScope || (isCreator ? "created" : isAssignee ? "assigned" : canReadAll ? "all" : "assigned")));
    }, [canReadAll, fromScope, isAssignee, isCreator, navigate]);

    const applyTaskState = useCallback((data) => {
        setTask(data);
        setDraft({
            ...data,
            deadline: toAppDate(data.deadline),
        });
        setAssignee(
            data.user_id
                ? {
                      id: data.user_id,
                      full_name: data.user_full_name || `User #${data.user_id}`,
                      email: data.user_email,
                  }
                : null,
        );
    }, []);

    const loadTask = useCallback(() => {
        requestAPI({
            requestPath: `user-tasks/${taskId}`,
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    applyTaskState(data);
                } else {
                    setError(data?.error || "Couldn't load task");
                }
            },
        });
    }, [applyTaskState, requestAPI, taskId]);

    const loadComments = useCallback(() => {
        requestAPI({
            requestPath: `user-tasks/${taskId}/comments`,
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setComments(data);
            },
        });
    }, [requestAPI, taskId]);

    useEffect(() => {
        loadTask();
        loadComments();
    }, [loadComments, loadTask]);

    const saveTask = useCallback(() => {
        const body = isCreator
            ? {
                  title: draft.title,
                  description: draft.description || null,
                  user_id: assignee?.id ?? draft.user_id,
                  status_id: draft.status_id,
                  priority: draft.priority,
                  attachment: draft.attachment || null,
                  deadline: draft.deadline ? getWriteableDate({ date: draft.deadline }) : null,
              }
            : { status_id: draft.status_id };

        requestAPI({
            requestPath: `user-tasks/${taskId}`,
            requestMethod: "PATCH",
            requestPostBody: body,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Task", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    showToast({ severity: "success", summary: "Updated", detail: "Task Updated", life: 1000 });
                    applyTaskState(updated);
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Task", life: 2000 });
                }
            },
        });
    }, [applyTaskState, assignee, draft, isCreator, requestAPI, showToast, taskId]);

    const deleteTask = useCallback(() => {
        requestAPI({
            requestPath: `user-tasks/${taskId}`,
            requestMethod: "DELETE",
            parseResponseBody: false,
            setLoading: setDeleting,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Task Deleted", life: 1000 });
                    goToList();
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete Task", life: 2000 });
                }
            },
        });
    }, [goToList, requestAPI, showToast, taskId]);

    const closeDialogComment = useCallback(() => {
        setDialogComment({ visible: false });
    }, []);

    const onCommentSaved = useCallback((saved, mode) => {
        if (mode === "add") {
            setComments((prev) => [...prev, saved]);
            return;
        }
        setComments((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
    }, []);

    const deleteComment = useCallback(
        (id) => {
            requestAPI({
                requestPath: `user-tasks/comments/${id}`,
                requestMethod: "DELETE",
                parseResponseBody: false,
                setLoading: setCommentLoading,
                onResponseReceieved: (_, responseCode) => {
                    if (responseCode === 204) {
                        setComments((prev) => prev.filter((c) => c.id !== id));
                    } else {
                        showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete Comment", life: 2000 });
                    }
                },
            });
        },
        [requestAPI, showToast],
    );

    if (loading) return <Loading />;
    if (error || !task) return <NoContent error={error || "Task Not Found"} />;

    return (
        <div className="flex-1 min-h-0 overflow-y-auto p-3 flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <div className="flex align-items-center gap-2">
                    <Button icon="pi pi-arrow-left" text rounded onClick={goToList} />
                    <span className={`font-semibold ${TEXT_SUBTITLE}`}>Task #{task.id}</span>
                    {isReadOnlyViewer ? <span className={`${TEXT_SMALL} text-color-secondary`}>View only</span> : null}
                </div>
                <div className="flex align-items-center gap-2">
                    {isCreator ? (
                        <ConfirmationWrapper action={deleteTask}>
                            <Button icon="pi pi-trash" severity="danger" outlined loading={deleting} />
                        </ConfirmationWrapper>
                    ) : null}
                    {canEditStatus ? <Button label="Save" severity="warning" loading={saving} onClick={saveTask} /> : null}
                </div>
            </div>

            <FloatLabel className="mt-3">
                <InputText
                    className="w-full"
                    value={draft?.title || ""}
                    disabled={!isCreator || saving}
                    onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label>Title</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <InputTextarea
                    className="w-full"
                    rows={4}
                    value={draft?.description || ""}
                    disabled={!isCreator || saving}
                    onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value || null }))}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
                <label>Description</label>
            </FloatLabel>

            {isCreator ? (
                <div className="mt-5">
                    <UserSearch
                        className="w-full"
                        value={assignee}
                        onChange={setAssignee}
                        disabled={saving}
                        floatLabel
                        label="Assignee"
                        inputId="task-detail-assignee"
                    />
                </div>
            ) : (
                <div className={`${TEXT_SMALL} mt-5`}>
                    Assignee: {task.user_full_name || EMPTY_VALUE} | Created by: {task.created_by_full_name || EMPTY_VALUE}
                </div>
            )}

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={selectedStatus}
                    options={statusOptions}
                    optionLabel="title"
                    placeholder={statusOptions.length ? "Select status" : "No statuses available"}
                    onChange={(e) => setDraft((prev) => ({ ...prev, status_id: e.value?.id }))}
                    disabled={!canEditStatus || saving || !statusOptions.length}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label>Status</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Dropdown
                    className="w-full"
                    value={selectedPriority}
                    options={TASK_PRIORITIES}
                    optionLabel="label"
                    placeholder="Select priority"
                    onChange={(e) => setDraft((prev) => ({ ...prev, priority: e.value?.id }))}
                    disabled={!isCreator || saving}
                    pt={{ input: { className: TEXT_NORMAL }, item: { className: TEXT_NORMAL } }}
                />
                <label>Priority</label>
            </FloatLabel>

            <FloatLabel className="mt-5">
                <Calendar
                    className="w-full"
                    value={draft?.deadline || null}
                    onChange={(e) => setDraft((prev) => ({ ...prev, deadline: e.value || null }))}
                    disabled={!isCreator || saving}
                    showTime
                    hourFormat="24"
                    showIcon
                    showButtonBar
                    dateFormat="dd/mm/yy"
                />
                <label>Deadline</label>
            </FloatLabel>

            <div className="mt-5">
                {isCreator ? (
                    <FileInput
                        label="Attachment"
                        type="image"
                        cdn_url={draft?.attachment}
                        setCDNUrl={(attachment) => setDraft((prev) => ({ ...prev, attachment }))}
                        disabled={saving}
                    />
                ) : draft?.attachment ? (
                    <img src={draft.attachment} alt="attachment" className="max-w-full border-round" style={{ maxHeight: 240 }} />
                ) : (
                    <span className={TEXT_SMALL}>No attachment</span>
                )}
            </div>

            <div className={`${TEXT_SMALL} text-color-secondary`}>
                Created {getReadableDate({ date: task.created_on })} by {task.created_by_full_name || EMPTY_VALUE}
                {task.deadline ? ` · Due ${getReadableDate({ date: task.deadline })}` : ""}
            </div>

            <div className="border-top-1 border-gray-300 pt-3 flex flex-column gap-3">
                <div className="flex align-items-center justify-content-between gap-2">
                    <span className={`font-semibold ${TEXT_SUBTITLE}`}>Comments</span>
                    {canComment ? (
                        <Button
                            icon="pi pi-plus"
                            label="Add Comment"
                            size="small"
                            severity="warning"
                            onClick={() => setDialogComment({ visible: true, taskId, closeDialog: closeDialogComment, onSaved: onCommentSaved })}
                        />
                    ) : null}
                </div>

                {comments.map((comment) => {
                    const own = isOwnComment(comment);
                    return (
                        <div key={comment.id} className="bg-gray-100 border-round p-3 flex flex-column gap-2">
                            <div className="flex justify-content-between gap-2">
                                <span className={`${TEXT_SMALL} font-semibold`}>
                                    {comment.created_by_full_name || EMPTY_VALUE} · {getReadableDate({ date: comment.created_on })}
                                </span>
                                {own ? (
                                    <div className="flex gap-2">
                                        <i
                                            className="pi pi-pencil cursor-pointer"
                                            onClick={() =>
                                                setDialogComment({
                                                    visible: true,
                                                    taskId,
                                                    comment,
                                                    closeDialog: closeDialogComment,
                                                    onSaved: onCommentSaved,
                                                })
                                            }
                                        />
                                        <ConfirmationWrapper action={() => deleteComment(comment.id)}>
                                            <i className={`pi pi-trash cursor-pointer text-red-500 ${commentLoading ? "opacity-50" : ""}`} />
                                        </ConfirmationWrapper>
                                    </div>
                                ) : null}
                            </div>
                            <span className={TEXT_NORMAL}>{comment.comment}</span>
                            {comment.attachment ? (
                                <img src={comment.attachment} alt="comment" className="max-w-full border-round" style={{ maxHeight: 160 }} />
                            ) : null}
                        </div>
                    );
                })}
            </div>

            {dialogComment?.visible ? <DialogTaskComment {...dialogComment} /> : null}
        </div>
    );
}
